import type { SVGProps } from 'react';

interface KeyBoardShortcut {
  withMeta: boolean;
  withShift?: boolean;
  key: string;
}

interface ContextMenuBasicItemProps<T> {
  action: (
    params: T,
    event:
      | React.MouseEvent<HTMLElement>
      | React.TouchEvent<HTMLElement>
      | React.KeyboardEvent<HTMLElement>
      | KeyboardEvent
  ) => void;
  commandDescription: string;
  afterCommandDescription?: string;
  tag?: React.FC<unknown>;
  isFirstMenuItem?: boolean;
  isLastMenuItem?: boolean;
  translationKey?: string;
  disabled?: boolean;
  keyboardShortcut?: KeyBoardShortcut;
  textColor?: string;
}

/**
 * @deprecated in the 2.0, use ContextMenuItemPropsWithSvgIcon
 */
interface ContextMenuItemPropsWithIconClassName<T> extends ContextMenuBasicItemProps<T> {
  iconClassName: string;
  svgIcon?: never;
  showProTag?: boolean;
  showBetaTag?: boolean;
  iconOpacity?: 0 | 20 | 40 | 60 | 80 | 100;
}

interface ContextMenuItemPropsWithSvgIcon<T> extends ContextMenuBasicItemProps<T> {
  svgIcon: (props: SVGProps<SVGSVGElement>) => React.JSX.Element;
  iconClassName?: never;
  showProTag?: boolean;
  showBetaTag?: boolean;
}

interface ContextMenuItemPropsWithoutSvgIcon<T> extends ContextMenuBasicItemProps<T> {
  svgIcon?: never;
  iconClassName?: never;
  showProIcon?: boolean;
}

export type ContextMenuSeparator = {
  type: 'separator';
  disabled?: boolean;
};

export type ContextMenuHeader = {
  type: 'header';
  text: string;
  disabled?: boolean;
};

export interface ContextMenuBasicItemSubmenu<T> {
  type: 'submenu';
  label: string;
  tag?: React.FC<unknown>;
  disabled?: boolean;
  items: ContextMenuItemList<T>[];
}

export interface ContextMenuSwitchItem<T> extends ContextMenuBasicItemProps<T> {
  action: never;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  switchId: string;
  value: boolean;
  defaultValue?: boolean;
}

export function isSwitchItem<T>(item: ContextMenuItemProps<T>): item is ContextMenuSwitchItem<T> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (item as ContextMenuSwitchItem<any>).switchId !== undefined;
}

interface ContextMenuSubmenuWithSvgIcon<T> extends ContextMenuBasicItemSubmenu<T> {
  svgIcon: (props: SVGProps<SVGSVGElement>) => React.JSX.Element;
  iconClassName?: never;
}

interface ContextMenuSubmenuWithoutSvgIcon<T> extends ContextMenuBasicItemSubmenu<T> {
  svgIcon?: never;
  iconClassName?: never;
}

/**
 * @deprecated in the 2.0, use ContextMenuSubmenuWithSvgIcon
 */
interface ContextMenuSubmenuWithIconClassName<T> extends ContextMenuBasicItemSubmenu<T> {
  iconClassName: string;
  svgIcon?: never;
}

export type ContextMenuItemProps<T> =
  | ContextMenuItemPropsWithoutSvgIcon<T>
  | ContextMenuItemPropsWithSvgIcon<T>
  | ContextMenuItemPropsWithIconClassName<T>;
export type ContextMenuItemSubmenu<T> =
  | ContextMenuSubmenuWithoutSvgIcon<T>
  | ContextMenuSubmenuWithSvgIcon<T>
  | ContextMenuSubmenuWithIconClassName<T>;

export type ContextMenuItemList<T> =
  | ContextMenuItemProps<T>
  | ContextMenuItemSubmenu<T>
  | ContextMenuSeparator
  | ContextMenuHeader
  | ContextMenuSwitchItem<T>;
